import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel, getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {ColumnFilter} from "components/dataTable/columnFilter.jsx";
import {useEffect, useState, useCallback, useRef} from "react";
import DownloadBtn from "./downloadButton.jsx";
import DebouncedInput from "./debouncedInput.jsx";

export const DataTable = ({columns, defaultSorting, dataFetch, refreshDependencies, exportFileName, ...props}) => {
  const [sorting, setSorting] = useState(defaultSorting || []);
  const [search, setSearch] = useState('')
  const [customFilters, setCustomFilters] = useState([]);
  const [columnFilters, setColumnFilters] = useState([])
  const [loading, setLoading] = useState(false)
  const [paginationData, setPaginationData] = useState({totalRows: 0, totalPages: 0});
  const [pagination, setPagination] = useState({pageIndex: 0, pageSize: 10});
  const [data, setData] = useState([])
  const hasInitialized = useRef(false);


  const refreshData = useCallback(async (exportData = false) => {
    if (!exportData) setLoading(true)
    let pageSize = pagination.pageSize;
    let pageIndex = pagination.pageIndex;
    if (exportData) {
      pageSize = null;
      pageIndex = 0;
    }

    const filters = columnFilters.reduce((previousValue, currentValue) => {
      return [...previousValue, ...currentValue.value]
    }, [])

    const sorts = sorting ? sorting.map(sort => ([sort.id, sort.desc ? 'DESC' : 'ASC'])) : [['id', 'desc']]

    const data = await dataFetch(
      pageSize,
      pageIndex + 1,
      sorts,
      search,
      [...customFilters, ...filters]
    );

    if (!exportData) {
      setPaginationData({
        totalRows: data.totalRows,
        totalPages: data.totalPages,
      });
      setData(data.data);
    }
    if (!exportData) setLoading(false)
    return data;
  }, [pagination.pageSize, pagination.pageIndex, columnFilters, sorting, search, customFilters, dataFetch]);

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    rowCount: paginationData.totalRows,
    pageCount: paginationData.totalPages,
    manualPagination: true,
    manualFiltering: true,
    autoResetPageIndex: false,
    onColumnFiltersChange: setColumnFilters,
    state: {
      sorting,
      pagination,
      columnFilters
    }
  });

  useEffect(() => {
    if (!dataFetch || !hasInitialized.current) return;
    refreshData().then();
  }, [search, pagination.pageIndex, pagination.pageSize, sorting, columnFilters, refreshDependencies, refreshData]);

  // Initial load
  useEffect(() => {
    if (dataFetch) {
      hasInitialized.current = true;
      refreshData().then();
    }
  }, [dataFetch]);

  useEffect(() => {
    table.resetPagination()
  }, [columnFilters]);

  return (
    <div className="p-2 text-black fill-gray-800">
      <div className="flex justify-end mb-2">
        <DownloadBtn refreshData={refreshData} columns={columns} totalRows={paginationData.totalRows}
                     fileName={exportFileName}/>
      </div>
      <div className={'overflow-x-auto'}>
        <table className="border w-full text-left">
          <thead className="bg-gray-200 text-gray-500">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th key={header.id} className="capitalize px-3.5 py-2">
                <span className="inline-flex">
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext()
                )}
                  {header.column.getIsSorted() ? (
                    header.column.getIsSorted() === "desc" ? (
                      <div>▼</div>
                    ) : (
                      <div>▲</div>
                    )
                  ) : null}
                  </span>
                  {header.column.getCanFilter() ? (
                    <div>
                      {header.column.columnDef.customFilter ? header.column.columnDef.customFilter(header.column, table) :
                        <ColumnFilter column={header.column} table={table}/>}
                    </div>
                  ) : null}
                </th>
              ))}
            </tr>
          ))}
          </thead>
          {loading ? <tbody>
            <tr>
              <td rowSpan={table.getState().pagination.pageSize} colSpan={columns.length}>Cargando...</td>
            </tr>
            </tbody> :
            <tbody>
            {table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row, i) => (
                <tr
                  key={row.id}
                  className={`
                ${i % 2 === 0 ? "bg-gray-100" : "bg-gray-200"}
                `}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-3.5 py-2">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr className="text-center h-32">
                <td colSpan={columns.length}>No hay datos para mostrar</td>
              </tr>
            )}
            </tbody>}
        </table>
      </div>
      {/* pagination */}
      <div className="flex items-center justify-end mt-2 gap-2">
        <button
          onClick={() => {
            table.previousPage();
          }}
          disabled={!table.getCanPreviousPage()}
          className="p-1 border border-gray-300 px-2 disabled:opacity-30"
        >
          {"<"}
        </button>
        <button
          onClick={() => {
            table.nextPage();
          }}
          disabled={!table.getCanNextPage()}
          className="p-1 border border-gray-300 px-2 disabled:opacity-30"
        >
          {">"}
        </button>

        <span className="flex items-center gap-1">
          <div>Página</div>
          <strong>
            {table.getState().pagination.pageIndex + 1} de{" "}
            {table.getPageCount()}
          </strong>
        </span>
        <span className="flex items-center gap-1">
          | Ir a página:
          <DebouncedInput
            type="number"
            value={''}
            onChange={(value) => {
              const page = value ? Number(value) - 1 : 0;
              table.setPageIndex(page);
            }}
            className="border p-1 rounded w-16 bg-transparent"
          />
        </span>
        <select
          value={table.getState().pagination.pageSize}
          onChange={(e) => {
            table.setPageSize(Number(e.target.value));
          }}
          className="p-2 bg-transparent"
        >
          {[10, 20, 30, 50].map((pageSize) => (
            <option key={pageSize} value={pageSize}>
              Mostrar {pageSize}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};
